import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MapPin,
  Star,
  Phone,
  Globe,
  Clock,
  Image as ImageIcon,
  Tag,
  Plus,
  Edit,
  Trash2,
  Send,
  Sparkles,
  Loader2,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Upload,
  Calendar,
  BarChart3,
  MessageSquare,
  Eye,
  MoreVertical,
  RefreshCw,
  Wand2,
  ChevronDown,
  ExternalLink,
  TrendingUp,
  Search,
  MousePointerClick,
  Navigation,
  X,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Progress } from '../../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { api } from '../../context/AuthContext';
import { useAuth } from '../../context/AuthContext';
import { useTaskState } from '../../context/TaskStateContext';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const POST_STATUS_BADGES = {
  draft:            { label: 'Draft',            color: 'bg-slate-100 text-slate-700' },
  humanized:        { label: 'Humanized',        color: 'bg-violet-100 text-violet-700' },
  pending_approval: { label: 'Pending Approval', color: 'bg-amber-100 text-amber-700' },
  approved:         { label: 'Approved',         color: 'bg-blue-100 text-blue-700' },
  scheduled:        { label: 'Scheduled',        color: 'bg-cyan-100 text-cyan-700' },
  published:        { label: 'Published',        color: 'bg-emerald-100 text-emerald-700' },
  rejected:         { label: 'Rejected',         color: 'bg-red-100 text-red-700' },
  failed:           { label: 'Failed',           color: 'bg-red-100 text-red-700' },
};

const CTA_OPTIONS = [
  { value: '', label: 'No CTA' },
  { value: 'BOOK', label: 'Book' },
  { value: 'ORDER', label: 'Order' },
  { value: 'SHOP', label: 'Shop' },
  { value: 'LEARN_MORE', label: 'Learn More' },
  { value: 'SIGN_UP', label: 'Sign Up' },
  { value: 'CALL', label: 'Call' },
];

const INITIAL_POST = {
  summary: '',
  post_type: 'whats_new',
  cta_type: '',
  cta_url: '',
  event_start: '',
  event_end: '',
  offer_coupon: '',
  offer_terms: '',
  image_data: null,
  scheduled_at: '',
};

// ── Profile Overview Tab ──────────────────────────────────────────

function ProfileTab() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [audit, setAudit] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sugLoading, setSugLoading] = useState(false);

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    setLoading(true);
    try {
      const [profileRes, auditRes] = await Promise.all([
        api.get('/gbp/profile'),
        api.get('/gbp/audit'),
      ]);
      setProfile(profileRes.data);
      setAudit(auditRes.data);
    } catch (err) {
      if (err.response?.status === 422 && err.response?.data?.detail === 'gbp_no_location_selected') {
        setProfile('no_location');
      } else if (err.response?.status === 400) {
        toast.error('Please connect your Google Business Profile first.');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadSuggestions = async () => {
    setSugLoading(true);
    try {
      const res = await api.get('/gbp/suggestions');
      setSuggestions(res.data.suggestions || []);
    } catch {
      toast.error('Failed to load suggestions');
    } finally {
      setSugLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  if (!profile) {
    return (
      <Card className="gds-card">
        <CardContent className="p-12 text-center">
          <div className="w-16 h-16 rounded-2xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-amber-600" />
          </div>
          <h3 className="font-heading font-semibold text-lg mb-2">No Business Profile Found</h3>
          <p className="text-slate-600 dark:text-slate-400 max-w-md mx-auto mb-6">
            Connect your Google Business Profile in Integrations to see your profile data, audit score, and optimization suggestions.
          </p>
          <Button
            className="gds-btn-primary"
            onClick={() => navigate('/dashboard/integrations')}
          >
            <MapPin className="w-4 h-4 mr-2" />
            Connect Google Business Profile
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (profile === 'no_location') {
    return (
      <Card className="gds-card">
        <CardContent className="p-12 text-center">
          <div className="w-16 h-16 rounded-2xl bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center mx-auto mb-4">
            <MapPin className="w-8 h-8 text-indigo-600" />
          </div>
          <h3 className="font-heading font-semibold text-lg mb-2">Select a Business Location</h3>
          <p className="text-slate-600 dark:text-slate-400 max-w-md mx-auto mb-6">
            Your Google Business Profile is connected. Go to Integrations and select an active location to load your profile data.
          </p>
          <Button
            className="gds-btn-primary"
            onClick={() => navigate('/dashboard/integrations')}
          >
            <MapPin className="w-4 h-4 mr-2" />
            Go to Integrations
          </Button>
        </CardContent>
      </Card>
    );
  }

  const auditScore = audit?.overall_score ?? 0;

  return (
    <div className="space-y-6">
      {/* Business Info + Audit Score */}
      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="gds-card lg:col-span-2">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <MapPin className="w-5 h-5 text-indigo-500" />
              Business Profile
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <InfoRow icon={MapPin} label="Business Name" value={profile.business_name} />
              <InfoRow icon={MapPin} label="Address" value={profile.address} />
              <InfoRow icon={Phone} label="Phone" value={profile.phone} />
              <InfoRow icon={Globe} label="Website" value={profile.website_url} isLink />
              <InfoRow icon={Tag} label="Categories" value={profile.categories?.join(', ')} />
              <InfoRow icon={ImageIcon} label="Photos" value={`${profile.photo_count} photos`} />
              <InfoRow icon={Star} label="Reviews" value={`${profile.total_reviews} reviews (${profile.average_rating} avg)`} />
              <InfoRow icon={Clock} label="Hours" value={profile.hours && Object.keys(profile.hours).length > 0 ? 'Set' : 'Not set'} />
            </div>
            {profile.description && (
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase mb-1">Description</p>
                <p className="text-sm text-slate-700 dark:text-slate-300 bg-slate-50 dark:bg-slate-800/50 p-3 rounded-lg">
                  {profile.description}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Audit Score Card */}
        <Card className="gds-card">
          <CardHeader>
            <CardTitle className="font-heading text-base">Profile Score</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col items-center gap-4">
            <div className="relative w-32 h-32">
              <svg className="w-32 h-32 -rotate-90" viewBox="0 0 120 120">
                <circle cx="60" cy="60" r="52" fill="none" stroke="currentColor" strokeWidth="8"
                  className="text-slate-200 dark:text-slate-700" />
                <circle cx="60" cy="60" r="52" fill="none" strokeWidth="8" strokeLinecap="round"
                  strokeDasharray={`${(auditScore / 100) * 327} 327`}
                  className={auditScore >= 70 ? 'text-emerald-500' : auditScore >= 40 ? 'text-amber-500' : 'text-red-500'} />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-3xl font-bold font-heading">{auditScore}</span>
              </div>
            </div>
            <p className="text-sm text-slate-500 text-center">
              {auditScore >= 80 ? 'Excellent' : auditScore >= 60 ? 'Good' : auditScore >= 40 ? 'Needs Work' : 'Poor'}
            </p>

            {audit?.issues?.length > 0 && (
              <div className="w-full space-y-2 mt-2">
                <p className="text-xs font-medium text-slate-500 uppercase">Issues</p>
                {audit.issues.map((issue, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm">
                    <XCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-600 dark:text-slate-400">{issue}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Optimization Suggestions */}
      <Card className="gds-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="font-heading">AI Optimization Suggestions</CardTitle>
              <CardDescription>Actionable tips to improve your profile visibility</CardDescription>
            </div>
            <Button variant="outline" onClick={loadSuggestions} disabled={sugLoading}>
              {sugLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
              {suggestions.length > 0 ? 'Refresh' : 'Get Suggestions'}
            </Button>
          </div>
        </CardHeader>
        {suggestions.length > 0 && (
          <CardContent className="space-y-3">
            {suggestions.map((s, i) => (
              <div key={i} className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="font-medium">{s.title}</h4>
                  <Badge variant="outline" className={
                    s.priority === 'high' ? 'bg-red-50 text-red-700 border-red-200' :
                    s.priority === 'medium' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                    'bg-blue-50 text-blue-700 border-blue-200'
                  }>{s.priority}</Badge>
                  {s.impact && <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">{s.impact}</Badge>}
                </div>
                <p className="text-sm text-slate-600 dark:text-slate-400">{s.description}</p>
              </div>
            ))}
          </CardContent>
        )}
      </Card>
    </div>
  );
}

function InfoRow({ icon: Icon, label, value, isLink }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3">
      <Icon className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
      <div className="min-w-0">
        <p className="text-xs text-slate-500">{label}</p>
        {isLink && value ? (
          <a href={value.startsWith('http') ? value : `https://${value}`} target="_blank" rel="noopener noreferrer"
             className="text-sm text-indigo-600 hover:underline truncate block">{value}</a>
        ) : (
          <p className="text-sm font-medium truncate">{value}</p>
        )}
      </div>
    </div>
  );
}

// ── Posts Tab ──────────────────────────────────────────────────────

function PostsTab() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [composerOpen, setComposerOpen] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [form, setForm] = useState({ ...INITIAL_POST });
  const [aiGenerating, setAiGenerating] = useState(false);
  const [humanizing, setHumanizing] = useState(null);
  const [publishing, setPublishing] = useState(null);
  const [submitting, setSubmitting] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);
  const gbpAiTask = useTaskState('gbp-ai-generate');

  useEffect(() => { fetchPosts(); }, []);

  const fetchPosts = async () => {
    try {
      const res = await api.get('/gbp/posts');
      setPosts(res.data?.items || res.data || []);
    } catch {
      console.error('Failed to fetch posts');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be under 5MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setForm(f => ({ ...f, image_data: reader.result }));
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleAIGenerate = async () => {
    setAiGenerating(true);
    gbpAiTask.start();
    try {
      const res = await api.post('/ai/generate-gbp-post', {
        business_name: 'My Business',
        business_type: '',
        post_type: form.post_type,
        topic: form.summary || 'general business update',
        keywords: [],
        tone: 'professional',
      });
      setForm(f => ({
        ...f,
        summary: res.data.content,
        cta_type: res.data.suggested_cta_type || '',
        cta_url: res.data.suggested_cta_url || '',
      }));
      toast.success('AI post generated!');
      gbpAiTask.complete(res.data);
    } catch {
      toast.error('Failed to generate AI post');
      gbpAiTask.fail('Failed to generate AI post');
    } finally {
      setAiGenerating(false);
    }
  };

  const handleSavePost = async () => {
    if (!form.summary.trim()) {
      toast.error('Post content is required');
      return;
    }
    try {
      if (editingPost) {
        await api.put(`/gbp/posts/${editingPost.post_id}`, {
          summary: form.summary,
          post_type: form.post_type,
          cta_type: form.cta_type || null,
          cta_url: form.cta_url || null,
          event_start: form.event_start || null,
          event_end: form.event_end || null,
          offer_coupon: form.offer_coupon || null,
          offer_terms: form.offer_terms || null,
          image_data: form.image_data || null,
          scheduled_at: form.scheduled_at || null,
        });
        toast.success('Post updated');
      } else {
        await api.post('/gbp/posts', {
          summary: form.summary,
          post_type: form.post_type,
          cta_type: form.cta_type || null,
          cta_url: form.cta_url || null,
          event_start: form.event_start || null,
          event_end: form.event_end || null,
          offer_coupon: form.offer_coupon || null,
          offer_terms: form.offer_terms || null,
          image_data: form.image_data || null,
          scheduled_at: form.scheduled_at || null,
        });
        toast.success('Post saved as draft');
      }
      resetComposer();
      fetchPosts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save post');
    }
  };

  const resetComposer = () => {
    setComposerOpen(false);
    setEditingPost(null);
    setForm({ ...INITIAL_POST });
    setImagePreview(null);
  };

  const openEdit = (post) => {
    setEditingPost(post);
    setForm({
      summary: post.summary,
      post_type: post.post_type,
      cta_type: post.cta_type || '',
      cta_url: post.cta_url || '',
      event_start: post.event_start || '',
      event_end: post.event_end || '',
      offer_coupon: post.offer_coupon || '',
      offer_terms: post.offer_terms || '',
      image_data: post.image_data || null,
      scheduled_at: post.scheduled_at || '',
    });
    setImagePreview(post.image_data || null);
    setComposerOpen(true);
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Delete this post?')) return;
    try {
      await api.delete(`/gbp/posts/${postId}`);
      setPosts(p => p.filter(x => x.post_id !== postId));
      toast.success('Post deleted');
    } catch {
      toast.error('Failed to delete post');
    }
  };

  const handleHumanize = async (postId) => {
    setHumanizing(postId);
    try {
      const res = await api.post(`/gbp/posts/${postId}/humanize`);
      toast.success(`Humanized! Score: ${res.data.naturalness_score}/100`);
      fetchPosts();
    } catch {
      toast.error('Failed to humanize post');
    } finally {
      setHumanizing(null);
    }
  };

  const handleSubmitForApproval = async (postId) => {
    setSubmitting(postId);
    try {
      await api.post(`/gbp/posts/${postId}/submit-for-approval`);
      toast.success('Submitted for approval');
      fetchPosts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit');
    } finally {
      setSubmitting(null);
    }
  };

  const handlePublish = async (postId) => {
    setPublishing(postId);
    try {
      await api.post(`/gbp/posts/${postId}/publish`);
      toast.success('Published to Google Business Profile!');
      fetchPosts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to publish');
    } finally {
      setPublishing(null);
    }
  };

  const statusBadge = (status) => {
    const cfg = POST_STATUS_BADGES[status] || POST_STATUS_BADGES.draft;
    return <Badge className={cfg.color}>{cfg.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading font-semibold text-lg">GBP Posts</h2>
          <p className="text-sm text-slate-500">{posts.length} posts total</p>
        </div>
        <Button className="gds-btn-primary" onClick={() => { resetComposer(); setComposerOpen(true); }}>
          <Plus className="w-4 h-4 mr-2" />
          New Post
        </Button>
      </div>

      {/* Composer Dialog */}
      <Dialog open={composerOpen} onOpenChange={(open) => { if (!open) resetComposer(); }}>
        <DialogContent className="sm:max-w-[640px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{editingPost ? 'Edit Post' : 'Create GBP Post'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {/* Post Type */}
            <div className="space-y-2">
              <Label>Post Type</Label>
              <div className="flex gap-2">
                {[
                  { value: 'whats_new', label: "What's New" },
                  { value: 'event', label: 'Event' },
                  { value: 'offer', label: 'Offer' },
                ].map(t => (
                  <Button key={t.value} variant={form.post_type === t.value ? 'default' : 'outline'}
                    size="sm" onClick={() => setForm(f => ({ ...f, post_type: t.value }))}>
                    {t.label}
                  </Button>
                ))}
              </div>
            </div>

            {/* Post Content */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Post Content <span className="text-red-500">*</span></Label>
                <span className={`text-xs ${form.summary.length > 1500 ? 'text-red-500' : 'text-slate-500'}`}>
                  {form.summary.length}/1500
                </span>
              </div>
              <Textarea rows={5} maxLength={1500} placeholder="Write your post content..."
                value={form.summary} onChange={(e) => setForm(f => ({ ...f, summary: e.target.value }))} />
              <Button variant="outline" size="sm" onClick={handleAIGenerate} disabled={aiGenerating || gbpAiTask.running} className="w-full">
                {aiGenerating || gbpAiTask.running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                {aiGenerating || gbpAiTask.running ? 'Generating...' : 'Generate with AI'}
              </Button>
            </div>

            {/* Image Upload */}
            <div className="space-y-2">
              <Label>Image (optional)</Label>
              <div className="border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-xl p-4 text-center cursor-pointer hover:border-indigo-300 transition-colors"
                onClick={() => fileInputRef.current?.click()}>
                {imagePreview ? (
                  <div className="relative">
                    <img src={imagePreview} alt="Preview" className="max-h-40 mx-auto rounded-lg" />
                    <Button variant="ghost" size="icon" className="absolute top-0 right-0"
                      onClick={(e) => { e.stopPropagation(); setForm(f => ({ ...f, image_data: null })); setImagePreview(null); }}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="py-4">
                    <Upload className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                    <p className="text-sm text-slate-500">Click to upload (max 5MB, JPG/PNG)</p>
                  </div>
                )}
              </div>
              <input ref={fileInputRef} type="file" accept="image/jpeg,image/png" className="hidden" onChange={handleImageUpload} />
            </div>

            {/* CTA */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>CTA Type</Label>
                <select className="w-full h-10 px-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 text-sm"
                  value={form.cta_type} onChange={(e) => setForm(f => ({ ...f, cta_type: e.target.value }))}>
                  {CTA_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div className="space-y-2">
                <Label>CTA URL</Label>
                <Input placeholder="https://..." value={form.cta_url}
                  onChange={(e) => setForm(f => ({ ...f, cta_url: e.target.value }))} />
              </div>
            </div>

            {/* Event Fields */}
            {form.post_type === 'event' && (
              <div className="grid grid-cols-2 gap-4 p-3 rounded-xl bg-slate-50 dark:bg-slate-800/50">
                <div className="space-y-2">
                  <Label>Event Start</Label>
                  <Input type="datetime-local" value={form.event_start}
                    onChange={(e) => setForm(f => ({ ...f, event_start: e.target.value }))} />
                </div>
                <div className="space-y-2">
                  <Label>Event End</Label>
                  <Input type="datetime-local" value={form.event_end}
                    onChange={(e) => setForm(f => ({ ...f, event_end: e.target.value }))} />
                </div>
              </div>
            )}

            {/* Offer Fields */}
            {form.post_type === 'offer' && (
              <div className="grid grid-cols-2 gap-4 p-3 rounded-xl bg-slate-50 dark:bg-slate-800/50">
                <div className="space-y-2">
                  <Label>Coupon Code</Label>
                  <Input placeholder="SAVE20" value={form.offer_coupon}
                    onChange={(e) => setForm(f => ({ ...f, offer_coupon: e.target.value }))} />
                </div>
                <div className="space-y-2">
                  <Label>Terms</Label>
                  <Input placeholder="Valid until..." value={form.offer_terms}
                    onChange={(e) => setForm(f => ({ ...f, offer_terms: e.target.value }))} />
                </div>
              </div>
            )}

            {/* Schedule */}
            <div className="space-y-2">
              <Label>Schedule (optional)</Label>
              <Input type="datetime-local" value={form.scheduled_at}
                onChange={(e) => setForm(f => ({ ...f, scheduled_at: e.target.value }))} />
              <p className="text-xs text-slate-500">Leave empty to save as draft. Scheduled posts require a cron trigger.</p>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" onClick={resetComposer}>Cancel</Button>
            <Button className="gds-btn-primary" onClick={handleSavePost}>
              {editingPost ? 'Update Post' : 'Save Draft'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Posts List */}
      {posts.length === 0 ? (
        <Card className="gds-card">
          <CardContent className="p-12 text-center">
            <MessageSquare className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <h3 className="font-heading font-semibold mb-2">No Posts Yet</h3>
            <p className="text-slate-500 mb-4">Create your first Google Business Profile post</p>
            <Button className="gds-btn-primary" onClick={() => setComposerOpen(true)}>
              <Plus className="w-4 h-4 mr-2" /> Create Post
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {posts.map((post, i) => (
            <motion.div key={post.post_id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              className="p-4 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    {statusBadge(post.status)}
                    <Badge variant="outline" className="capitalize">{post.post_type?.replace('_', ' ')}</Badge>
                    {post.scheduled_at && <Badge variant="outline" className="bg-cyan-50 text-cyan-700 border-cyan-200"><Calendar className="w-3 h-3 mr-1" />{new Date(post.scheduled_at).toLocaleDateString()}</Badge>}
                  </div>
                  <p className="text-sm text-slate-700 dark:text-slate-300 line-clamp-3">{post.summary}</p>
                  {post.cta_type && (
                    <p className="text-xs text-slate-500 mt-1">CTA: {post.cta_type} {post.cta_url && `→ ${post.cta_url}`}</p>
                  )}
                  {post.created_at && (
                    <p className="text-xs text-slate-400 mt-2">{new Date(post.created_at).toLocaleString()}</p>
                  )}
                </div>

                {/* Thumbnail */}
                {post.image_data && (
                  <img src={post.image_data} alt="" className="w-16 h-16 rounded-lg object-cover flex-shrink-0" />
                )}

                {/* Actions */}
                <div className="flex items-center gap-1 flex-shrink-0">
                  {(post.status === 'draft' || post.status === 'humanized' || post.status === 'rejected') && (
                    <>
                      <Button variant="ghost" size="icon" title="Humanize"
                        disabled={humanizing === post.post_id}
                        onClick={() => handleHumanize(post.post_id)}>
                        {humanizing === post.post_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
                      </Button>
                      <Button variant="ghost" size="icon" title="Submit for Approval"
                        disabled={submitting === post.post_id}
                        onClick={() => handleSubmitForApproval(post.post_id)}>
                        {submitting === post.post_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                      </Button>
                    </>
                  )}
                  {(post.status === 'approved' || post.status === 'scheduled') && (
                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      disabled={publishing === post.post_id}
                      onClick={() => handlePublish(post.post_id)}>
                      {publishing === post.post_id ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <ExternalLink className="w-4 h-4 mr-1" />}
                      Publish
                    </Button>
                  )}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon"><MoreVertical className="w-4 h-4" /></Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {(post.status === 'draft' || post.status === 'humanized' || post.status === 'rejected') && (
                        <DropdownMenuItem onClick={() => openEdit(post)}>
                          <Edit className="w-4 h-4 mr-2" /> Edit
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem onClick={() => handleDelete(post.post_id)} className="text-red-600">
                        <Trash2 className="w-4 h-4 mr-2" /> Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>

              {/* Original vs Humanized */}
              {post.original_summary && post.status === 'humanized' && (
                <div className="mt-3 p-3 rounded-lg bg-violet-50 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-800">
                  <p className="text-xs font-medium text-violet-600 mb-1">Original (before humanization)</p>
                  <p className="text-xs text-slate-600 dark:text-slate-400 line-clamp-2">{post.original_summary}</p>
                </div>
              )}
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Reviews Tab ───────────────────────────────────────────────────

function ReviewsTab() {
  const navigate = useNavigate();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [apiRestricted, setApiRestricted] = useState(false);
  const [noLocationSelected, setNoLocationSelected] = useState(false);
  const [error, setError] = useState(null);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replying, setReplying] = useState(false);

  useEffect(() => { loadReviews(); }, []);

  const loadReviews = async () => {
    setApiRestricted(false);
    setNoLocationSelected(false);
    setError(null);
    try {
      const res = await api.get('/gbp/reviews');
      setReviews(res.data.reviews || []);
    } catch (err) {
      if (err.response?.status === 503) {
        setApiRestricted(true);
      } else if (err.response?.status === 422 && err.response?.data?.detail === 'gbp_no_location_selected') {
        setNoLocationSelected(true);
      } else {
        setError(err.response?.data?.detail || 'Failed to load reviews');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleReply = async (reviewName) => {
    if (!replyText.trim()) return;
    setReplying(true);
    try {
      await api.post(`/gbp/reviews/${encodeURIComponent(reviewName)}/reply`, { reply_text: replyText });
      toast.success('Reply posted!');
      setReplyingTo(null);
      setReplyText('');
      loadReviews();
    } catch {
      toast.error('Failed to post reply');
    } finally {
      setReplying(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  if (apiRestricted) {
    return (
      <Card className="gds-card border-amber-200 bg-amber-50/30">
        <CardContent className="p-12 text-center">
          <Star className="w-12 h-12 mx-auto mb-4 text-amber-400" />
          <h3 className="font-heading font-semibold mb-2">Reviews API Access Required</h3>
          <p className="text-slate-600 mb-2">
            Google restricts access to the My Business Reviews API. Your Google Cloud project needs explicit approval to read reviews.
          </p>
          <p className="text-sm text-slate-500">
            Enable <strong>My Business API</strong> in Google Cloud Console and request access from Google to use this feature.
          </p>
        </CardContent>
      </Card>
    );
  }

  if (noLocationSelected) {
    return (
      <Card className="gds-card">
        <CardContent className="p-12 text-center">
          <Star className="w-12 h-12 mx-auto mb-4 text-slate-300" />
          <h3 className="font-heading font-semibold mb-2">Select a Business Location</h3>
          <p className="text-slate-500 mb-6">
            Your Google Business Profile is connected. Please select an active location in Integrations to load your reviews.
          </p>
          <Button
            className="gds-btn-primary"
            onClick={() => navigate('/dashboard/integrations')}
          >
            <MapPin className="w-4 h-4 mr-2" />
            Go to Integrations
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    const isNotConnected = error.toLowerCase().includes('not connected') || error.toLowerCase().includes('is not connected');
    return (
      <Card className="gds-card">
        <CardContent className="p-12 text-center">
          <Star className="w-12 h-12 mx-auto mb-4 text-slate-300" />
          <h3 className="font-heading font-semibold mb-2">
            {isNotConnected ? 'Google Business Profile Not Connected' : 'Could Not Load Reviews'}
          </h3>
          <p className="text-slate-500 mb-6">
            {isNotConnected
              ? 'Connect your Google Business Profile to see and manage your reviews.'
              : error}
          </p>
          {isNotConnected ? (
            <Button
              className="gds-btn-primary"
              onClick={() => navigate('/dashboard/integrations')}
            >
              <MapPin className="w-4 h-4 mr-2" />
              Connect Google Business Profile
            </Button>
          ) : (
            <Button variant="outline" onClick={loadReviews}>
              Retry
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  if (reviews.length === 0) {
    return (
      <Card className="gds-card">
        <CardContent className="p-12 text-center">
          <Star className="w-12 h-12 mx-auto mb-4 text-slate-300" />
          <h3 className="font-heading font-semibold mb-2">No Reviews Yet</h3>
          <p className="text-slate-500">Reviews from your Google Business Profile will appear here once customers leave them.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-heading font-semibold text-lg">Google Reviews</h2>
        <Button variant="outline" size="sm" onClick={loadReviews}>
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>
      {reviews.map((review, i) => {
        const stars = review.starRating === 'FIVE' ? 5 : review.starRating === 'FOUR' ? 4 :
                      review.starRating === 'THREE' ? 3 : review.starRating === 'TWO' ? 2 : 1;
        const reviewName = review.name || review.reviewId || `review_${i}`;
        return (
          <Card key={reviewName} className="gds-card">
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-medium">{review.reviewer?.displayName?.charAt(0) || '?'}</span>
                  </div>
                  <div>
                    <p className="font-medium text-sm">{review.reviewer?.displayName || 'Anonymous'}</p>
                    <div className="flex items-center gap-1 mt-0.5">
                      {Array.from({ length: 5 }).map((_, si) => (
                        <Star key={si} className={`w-4 h-4 ${si < stars ? 'text-amber-400 fill-amber-400' : 'text-slate-300'}`} />
                      ))}
                    </div>
                    {review.comment && <p className="text-sm text-slate-600 dark:text-slate-400 mt-2">{review.comment}</p>}
                    {review.createTime && <p className="text-xs text-slate-400 mt-1">{new Date(review.createTime).toLocaleDateString()}</p>}

                    {review.reviewReply && (
                      <div className="mt-2 p-2 rounded-lg bg-slate-50 dark:bg-slate-800/50">
                        <p className="text-xs font-medium text-slate-500">Your reply</p>
                        <p className="text-sm text-slate-600 dark:text-slate-400">{review.reviewReply.comment}</p>
                      </div>
                    )}
                  </div>
                </div>
                {!review.reviewReply && (
                  <Button variant="outline" size="sm" onClick={() => { setReplyingTo(reviewName); setReplyText(''); }}>
                    <MessageSquare className="w-4 h-4 mr-1" /> Reply
                  </Button>
                )}
              </div>

              {replyingTo === reviewName && (
                <div className="mt-3 flex gap-2">
                  <Textarea rows={2} placeholder="Write your reply..." value={replyText}
                    onChange={(e) => setReplyText(e.target.value)} className="flex-1" />
                  <div className="flex flex-col gap-1">
                    <Button size="sm" onClick={() => handleReply(reviewName)} disabled={replying}>
                      {replying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setReplyingTo(null)}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

// ── Performance Tab ───────────────────────────────────────────────

function PerformanceTab() {
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  useEffect(() => { loadMetrics(); }, [days]);

  const loadMetrics = async () => {
    setLoading(true);
    try {
      const res = await api.get('/gbp/metrics', { params: { days } });
      setMetrics(res.data);
    } catch {
      // GBP not connected
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  if (!metrics || (!metrics.series?.length && !Object.keys(metrics.totals || {}).length)) {
    return (
      <Card className="gds-card">
        <CardContent className="p-12 text-center">
          <BarChart3 className="w-12 h-12 mx-auto mb-4 text-slate-300" />
          <h3 className="font-heading font-semibold mb-2">No Performance Data</h3>
          <p className="text-slate-500 mb-6">Connect your Google Business Profile to see performance metrics.</p>
          <Button
            className="gds-btn-primary"
            onClick={() => navigate('/dashboard/integrations')}
          >
            <MapPin className="w-4 h-4 mr-2" />
            Connect Google Business Profile
          </Button>
        </CardContent>
      </Card>
    );
  }

  const totals = metrics.totals || {};

  const impressions = (totals.BUSINESS_IMPRESSIONS_DESKTOP_MAPS || 0) +
    (totals.BUSINESS_IMPRESSIONS_DESKTOP_SEARCH || 0) +
    (totals.BUSINESS_IMPRESSIONS_MOBILE_MAPS || 0) +
    (totals.BUSINESS_IMPRESSIONS_MOBILE_SEARCH || 0);
  const clicks = totals.WEBSITE_CLICKS || 0;
  const calls = totals.CALL_CLICKS || 0;
  const directions = totals.BUSINESS_DIRECTION_REQUESTS || 0;

  const chartData = {};
  (metrics.series || []).forEach(pt => {
    if (!chartData[pt.date]) chartData[pt.date] = { date: pt.date };
    const key = pt.metric.includes('IMPRESSION') ? 'impressions' :
                pt.metric === 'WEBSITE_CLICKS' ? 'clicks' :
                pt.metric === 'CALL_CLICKS' ? 'calls' : 'directions';
    chartData[pt.date][key] = (chartData[pt.date][key] || 0) + pt.value;
  });
  const chartArray = Object.values(chartData).sort((a, b) => a.date.localeCompare(b.date));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-heading font-semibold text-lg">Performance Metrics</h2>
        <div className="flex gap-2">
          {[7, 30, 90].map(d => (
            <Button key={d} variant={days === d ? 'default' : 'outline'} size="sm" onClick={() => setDays(d)}>
              {d}d
            </Button>
          ))}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricCard icon={Eye} label="Views" value={impressions} color="indigo" />
        <MetricCard icon={Search} label="Searches / Clicks" value={clicks} color="emerald" />
        <MetricCard icon={Phone} label="Calls" value={calls} color="amber" />
        <MetricCard icon={Navigation} label="Direction Requests" value={directions} color="violet" />
      </div>

      {/* Charts */}
      {chartArray.length > 0 && (
        <>
          <Card className="gds-card">
            <CardHeader>
              <CardTitle className="font-heading text-base">Views Over Time</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={chartArray}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="impressions" stroke="#6366f1" strokeWidth={2} name="Impressions" dot={false} />
                  <Line type="monotone" dataKey="clicks" stroke="#10b981" strokeWidth={2} name="Clicks" dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card className="gds-card">
            <CardHeader>
              <CardTitle className="font-heading text-base">Actions Over Time</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={chartArray}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="calls" fill="#f59e0b" name="Calls" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="directions" fill="#8b5cf6" name="Directions" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

function MetricCard({ icon: Icon, label, value, color }) {
  const colorClasses = {
    indigo: 'bg-indigo-100 text-indigo-600 dark:bg-indigo-900/30',
    emerald: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30',
    amber: 'bg-amber-100 text-amber-600 dark:bg-amber-900/30',
    violet: 'bg-violet-100 text-violet-600 dark:bg-violet-900/30',
  };
  return (
    <Card className="gds-card">
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${colorClasses[color]}`}>
            <Icon className="w-5 h-5" />
          </div>
          <div>
            <p className="text-2xl font-bold font-heading">{value.toLocaleString()}</p>
            <p className="text-xs text-slate-500">{label}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Main Dashboard ────────────────────────────────────────────────

export default function GBPDashboardPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('profile');

  return (
    <div className="space-y-6" data-testid="gbp-dashboard">
      {/* Header */}
      <div>
        <h1 className="font-heading text-2xl font-bold mb-1">Google Business Profile</h1>
        <p className="text-slate-600 dark:text-slate-400">
          Manage your business profile, posts, reviews, and local SEO performance
        </p>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-100 dark:bg-slate-800 p-1 rounded-xl">
          <TabsTrigger value="profile" className="rounded-lg gap-2">
            <MapPin className="w-4 h-4" /> Profile
          </TabsTrigger>
          <TabsTrigger value="posts" className="rounded-lg gap-2">
            <MessageSquare className="w-4 h-4" /> Posts
          </TabsTrigger>
          <TabsTrigger value="reviews" className="rounded-lg gap-2">
            <Star className="w-4 h-4" /> Reviews
          </TabsTrigger>
          <TabsTrigger value="performance" className="rounded-lg gap-2">
            <BarChart3 className="w-4 h-4" /> Performance
          </TabsTrigger>
        </TabsList>

        <TabsContent value="profile"><ProfileTab /></TabsContent>
        <TabsContent value="posts"><PostsTab /></TabsContent>
        <TabsContent value="reviews"><ReviewsTab /></TabsContent>
        <TabsContent value="performance"><PerformanceTab /></TabsContent>
      </Tabs>
    </div>
  );
}
